import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Polls `/api/version` periodically and fires a one-time Sonner toast
// when the backend SHA rolls forward while the tab has been open —
// catches the rare case of a long-lived editor tab silently drifting
// off the latest deploy.
const POLL_INTERVAL_MS = 90_000; // 90 s — generous, matches our deploy cadence
const STORAGE_KEY = 'seertech_dismissed_build_drift_sha';

export default function useBuildVersionWatcher() {
  const initialShaRef = useRef(null);
  const toastedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/version`);
        const sha = res.data?.commit;
        if (!sha || cancelled) return;

        if (initialShaRef.current === null) {
          initialShaRef.current = sha;
          return;
        }

        if (sha === initialShaRef.current || toastedRef.current) return;

        // The SHA the user has dismissed before — re-firing for the same
        // drift would be annoying. We persist the dismissed SHA in
        // sessionStorage (cleared on tab close, so reopening the tab
        // gets a fresh check).
        let dismissed = '';
        try { dismissed = sessionStorage.getItem(STORAGE_KEY) || ''; } catch { /* ignore */ }
        if (dismissed === sha) return;

        toastedRef.current = true;
        toast.info('New version deployed', {
          id: `build-drift-${sha}`,
          description: `This page is running an older build (${initialShaRef.current}). Reload to pick up the latest.`,
          duration: Infinity,
          action: {
            label: 'Reload',
            onClick: () => window.location.reload(),
          },
          onDismiss: () => {
            try { sessionStorage.setItem(STORAGE_KEY, sha); } catch { /* ignore */ }
          },
        });
      } catch (err) {
        logger.warn('Build-version watcher failed', err);
      }
    };

    check(); // seed initial SHA
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
}
